import { FormHelperText, Grid } from '@mui/material';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import MDEditor from '@uiw/react-md-editor';
import { useAppContext } from '../../contexts/AppContext';
import FromSubmitButton from '../ui/forms/FormSubmitButton';
import useFormSubmitErrorAlert from '../ui/forms/useFormSubmitErrorAlert';
import CustomForm, { onSuccessCallback } from '../ui/forms/CustomForm';
import { VALIDATION_ERR_DELAY } from '../../config/AppConstants';
import { StoreQueryKeys } from '../../lib/api/store/store.service';
import { ViewStoreStatusDto } from '../../lib/api/store/dto/view-store-status.dto';
import { UpdateStoreStatusFeatureNoteDto } from '../../lib/api/store/dto/update-store-status-feature-note.dto';
import { updateStoreStatusFeatureNote } from '../../lib/api/store/store.service';

interface Props {
  storeStatus: ViewStoreStatusDto;
  onSuccess?: onSuccessCallback;
}

export default function UpdateStoreFeatureNoteForm({
  storeStatus,
  onSuccess,
}: Props) {
  const { handleShowMessage } = useAppContext();
  const queryClient = useQueryClient();

  const action = storeStatus.featureNote
    ? 'Update Store Note'
    : 'Add Store Note';

  //Mutations
  const updateMutation = useMutation(
    (updateStoreStatusFeatureNoteDto: UpdateStoreStatusFeatureNoteDto) =>
      updateStoreStatusFeatureNote(updateStoreStatusFeatureNoteDto),
  );

  const {
    handleSubmit,
    formState: { isSubmitting, isValid, isSubmitted },
    control,
    setError,
  } = useForm<UpdateStoreStatusFeatureNoteDto>({
    resolver: classValidatorResolver(UpdateStoreStatusFeatureNoteDto),
    mode: 'onTouched',
    defaultValues: {
      featureNote: storeStatus.featureNote,
    },
    delayError: VALIDATION_ERR_DELAY,
  });

  const { clearFormSubmitError, processFormSubmitError, serverErrorMessage } =
    useFormSubmitErrorAlert<UpdateStoreStatusFeatureNoteDto>();

  const onSubmit: SubmitHandler<UpdateStoreStatusFeatureNoteDto> = async (
    formData: UpdateStoreStatusFeatureNoteDto,
  ) => {
    clearFormSubmitError();

    try {
      let result: ViewStoreStatusDto;

      //Updating
      result = await updateMutation.mutateAsync(formData);
      queryClient.invalidateQueries([StoreQueryKeys.findStatus]);

      handleShowMessage('Store Note Updated');
      onSuccess?.(result);
    } catch (err: any) {
      processFormSubmitError(err, setError);
    }
  };

  return (
    <CustomForm
      serverErrorMessage={serverErrorMessage}
      handleSubmit={handleSubmit(onSubmit)}
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Controller
            name="featureNote"
            control={control}
            render={({
              field: { value, onChange, onBlur },
              fieldState: { error },
            }) => (
              <>
                <MDEditor
                  value={value ?? ''}
                  onChange={(val) => onChange(val ?? '')}
                  onBlur={onBlur}
                  height={400}
                  data-color-mode="light"
                  autoFocus
                  preview="live"
                  visibleDragbar={false}
                />
                {error && (
                  <FormHelperText error>{error.message}</FormHelperText>
                )}
              </>
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <FromSubmitButton
            isSubmitted={isSubmitted}
            isSubmitting={isSubmitting}
            isValid={isValid}
            text={action}
          />
        </Grid>
      </Grid>
    </CustomForm>
  );
}
